import { z } from 'zod';

import {
  ACTION_CARD_CATEGORIES,
  ACTION_CARD_GENERATED_BY_TYPES,
  ACTION_CARD_LINK_TYPES,
  ACTION_CARD_LINK_VISIBILITY,
  ACTION_CARD_SEVERITIES,
  ACTION_CARD_STATUSES,
  ACTION_CARD_SUBJECT_TYPES,
  ACTION_CARD_SUGGESTED_ACTION_TYPES,
  ACTION_CARD_TYPES,
  ACTION_POLICY_RISK_LEVELS,
  APPROVAL_MODES,
  CONTEXT_ACTION_MODES,
  CONTEXT_MATURITY_STAGES,
  CONTEXT_MONITORING_CADENCES,
  CONTEXT_MONITORING_PROFILE_KINDS,
  CONTEXT_MONITORING_STATUS,
  CONTEXT_TYPES,
} from '../constants/enums';

const SECRET_KEY_PATTERN = /(^|_|-)(authorization|api[_-]?key|access[_-]?token|refresh[_-]?token|bearer|oauth|password|secret|secret[_-]?ref|webhook[_-]?secret)(_|-|$)/i;

function isJsonText(value: string): boolean {
  try {
    JSON.parse(value);
    return true;
  } catch (error) {
    if (error instanceof SyntaxError) return false;
    return false;
  }
}

function containsSecretKey(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  if (Array.isArray(value)) return value.some((item) => containsSecretKey(item));
  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    if (SECRET_KEY_PATTERN.test(key)) return true;
    if (containsSecretKey(child)) return true;
  }
  return false;
}

const sanitizedJsonTextSchema = z
  .string()
  .min(1)
  .refine(isJsonText, { message: 'must be valid JSON text' })
  .refine((value) => !containsSecretKey(JSON.parse(value)), {
    message: 'sanitized JSON must not contain secret-bearing keys',
  });

const nullableIdSchema = z.string().min(1).nullable();

export const actionCardSurfaceStateSchema = z.enum([
  'loading',
  'empty',
  'normal',
  'stale',
  'overflow',
  'redacted',
  'forbidden',
  'error',
]);
export type ActionCardSurfaceState = z.infer<typeof actionCardSurfaceStateSchema>;

export const actionCardSupportRefSchema = z.object({
  entity_type: z.enum(ACTION_CARD_SUBJECT_TYPES),
  entity_id: z.string().min(1),
  link_type: z.enum(ACTION_CARD_LINK_TYPES),
  visibility: z.enum(ACTION_CARD_LINK_VISIBILITY).default('normal'),
});
export type ActionCardSupportRef = z.infer<typeof actionCardSupportRefSchema>;

export const actionCardProposalRequestSchema = z.object({
  context_id: z.string().min(1),
  subject_type: z.enum(ACTION_CARD_SUBJECT_TYPES),
  subject_id: z.string().min(1).nullable().optional(),
  title: z.string().min(1),
  summary: z.string().nullable().optional(),
  card_type: z.enum(ACTION_CARD_TYPES),
  category: z.enum(ACTION_CARD_CATEGORIES),
  severity: z.enum(ACTION_CARD_SEVERITIES).default('medium'),
  risk_level: z.enum(ACTION_POLICY_RISK_LEVELS).default('low'),
  confidence: z.number().min(0).max(1).nullable().optional(),
  priority_score: z.number().min(0).nullable().optional(),
  reason: z.string().min(1),
  suggested_action_type: z.enum(ACTION_CARD_SUGGESTED_ACTION_TYPES),
  command_preview_json: sanitizedJsonTextSchema,
  expected_effect_json: sanitizedJsonTextSchema.nullable().optional(),
  approval_mode: z.enum(APPROVAL_MODES),
  action_policy_ref: z.string().min(1).nullable().optional(),
  generated_by_type: z.enum(ACTION_CARD_GENERATED_BY_TYPES),
  generated_by_id: z.string().min(1),
  source_event_id: z.string().min(1).nullable().optional(),
  rule_run_id: z.string().min(1).nullable().optional(),
  agent_run_id: z.string().min(1).nullable().optional(),
  expires_at: z.string().min(1).nullable().optional(),
  assigned_actor_id: z.string().min(1).nullable().optional(),
  audience_role: z.string().min(1).nullable().optional(),
  dedupe_key: z.string().min(1).nullable().optional(),
  normalized_cause: z.string().min(1).nullable().optional(),
  supporting_refs: z.array(actionCardSupportRefSchema).default([]),
});
export type ActionCardProposalRequest = z.infer<typeof actionCardProposalRequestSchema>;

export const actionCardAcceptRequestSchema = z.object({
  actor_id: z.string().min(1),
  on_behalf_of_actor_id: z.string().min(1).nullable().optional(),
  authorized_via_type: z.string().min(1).nullable().optional(),
  authorized_via_id: z.string().min(1).nullable().optional(),
  payload_hash: z.string().min(1).nullable().optional(),
});
export type ActionCardAcceptRequest = z.infer<typeof actionCardAcceptRequestSchema>;

export const actionCardLifecycleRequestSchema = z.object({
  actor_id: z.string().min(1),
  reason: z.string().min(1).nullable().optional(),
  defer_until: z.string().min(1).nullable().optional(),
  replacement_action_card_id: z.string().min(1).nullable().optional(),
});
export type ActionCardLifecycleRequest = z.infer<typeof actionCardLifecycleRequestSchema>;

export const actionCardSummarySchema = z.object({
  id: z.string().min(1),
  context_id: z.string().min(1),
  subject_type: z.enum(ACTION_CARD_SUBJECT_TYPES),
  subject_id: nullableIdSchema,
  title: z.string().min(1),
  summary: z.string().nullable(),
  card_type: z.enum(ACTION_CARD_TYPES),
  category: z.enum(ACTION_CARD_CATEGORIES),
  severity: z.enum(ACTION_CARD_SEVERITIES),
  risk_level: z.enum(ACTION_POLICY_RISK_LEVELS),
  confidence: z.number().min(0).max(1).nullable(),
  priority_score: z.number().min(0).nullable(),
  reason: z.string().min(1),
  suggested_action_type: z.enum(ACTION_CARD_SUGGESTED_ACTION_TYPES),
  approval_mode: z.enum(APPROVAL_MODES),
  status: z.enum(ACTION_CARD_STATUSES),
  occurrence_count: z.number().int().min(1),
  last_seen_at: z.string().min(1),
  support_count: z.number().int().min(0),
  redacted_support_count: z.number().int().min(0),
  generated_by_type: z.enum(ACTION_CARD_GENERATED_BY_TYPES),
  generated_by_id: z.string().min(1),
  execution_status: z.string().min(1).nullable(),
});
export type ActionCardSummary = z.infer<typeof actionCardSummarySchema>;

const recommendationStreamSummarySchema = z.object({
  total: z.number().int().min(0),
  proposed: z.number().int().min(0),
  accepted: z.number().int().min(0),
  deferred: z.number().int().min(0),
  failed: z.number().int().min(0),
  terminal: z.number().int().min(0),
  redacted_support_refs: z.number().int().min(0),
});

export const recommendationStreamPacketSchema = z
  .object({
    packet_type: z.literal('recommendation_stream'),
    version: z.literal('v3.action-cards.1'),
    generated_at: z.string().min(1),
    context_id: z.string().min(1),
    surface_state: actionCardSurfaceStateSchema,
    cards: z.array(actionCardSummarySchema),
    summary: recommendationStreamSummarySchema,
  })
  .superRefine((packet, ctx) => {
    const terminal = packet.cards.filter((card) =>
      ['completed', 'rejected', 'expired', 'superseded', 'cancelled'].includes(card.status),
    ).length;
    const redactedSupport = packet.cards.reduce((sum, card) => sum + card.redacted_support_count, 0);
    if (
      packet.summary.total !== packet.cards.length ||
      packet.summary.proposed !== packet.cards.filter((card) => card.status === 'proposed').length ||
      packet.summary.accepted !== packet.cards.filter((card) => card.status === 'accepted').length ||
      packet.summary.deferred !== packet.cards.filter((card) => card.status === 'deferred').length ||
      packet.summary.failed !== packet.cards.filter((card) => card.status === 'failed').length ||
      packet.summary.terminal !== terminal ||
      packet.summary.redacted_support_refs !== redactedSupport
    ) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'summary counts must match packet contents' });
    }
  });
export type RecommendationStreamPacket = z.infer<typeof recommendationStreamPacketSchema>;

const controlRoomContextSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  context_type: z.enum(CONTEXT_TYPES),
  maturity_stage: z.enum(CONTEXT_MATURITY_STAGES),
  status: z.string().min(1),
  owner_user_id: nullableIdSchema,
  monitoring_profile_id: nullableIdSchema,
});

const controlRoomMonitoringProfileSchema = z.object({
  id: z.string().min(1),
  status: z.enum(CONTEXT_MONITORING_STATUS),
  profile_kind: z.enum(CONTEXT_MONITORING_PROFILE_KINDS),
  cadence: z.enum(CONTEXT_MONITORING_CADENCES),
  review_cadence: z.enum(CONTEXT_MONITORING_CADENCES).nullable(),
  action_mode: z.enum(CONTEXT_ACTION_MODES),
  saved_view_id: nullableIdSchema,
  default_lens_id: nullableIdSchema,
  next_review_at: z.string().min(1).nullable(),
  owner_actor_id: nullableIdSchema,
});

const controlRoomViewSchema = z.object({
  lens: z.object({
    id: z.string().min(1),
    slug: z.string().min(1),
    name: z.string().min(1),
    version: z.string().min(1),
  }).nullable(),
  saved_view: z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    root_context_id: nullableIdSchema,
  }).nullable(),
});

export const controlRoomPacketSchema = z
  .object({
    packet_type: z.literal('control_room'),
    version: z.literal('v3.control-room.1'),
    generated_at: z.string().min(1),
    context: controlRoomContextSchema,
    monitoring_profile: controlRoomMonitoringProfileSchema.nullable(),
    control_view: controlRoomViewSchema,
    activation_state: z.enum(['attention_field', 'setup_required', 'canonical']),
    recommendation_stream: recommendationStreamPacketSchema,
    warnings: z.array(z.string().min(1)),
  })
  .superRefine((packet, ctx) => {
    if (packet.activation_state !== 'canonical') return;
    if (!packet.monitoring_profile || packet.monitoring_profile.status !== 'active') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'canonical control room requires active monitoring profile',
      });
    }
    if (packet.monitoring_profile?.profile_kind !== 'control_room') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'canonical control room requires control_room profile kind',
      });
    }
    if (packet.context.maturity_stage !== 'governed') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'canonical control room requires governed context maturity',
      });
    }
    if (!packet.control_view.saved_view) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'canonical control room requires saved view',
      });
    }
  });
export type ControlRoomPacket = z.infer<typeof controlRoomPacketSchema>;
